package one.healthium.carnivore.presentation.navigation.root

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import com.arkivanov.decompose.extensions.compose.stack.Children
import com.arkivanov.decompose.extensions.compose.stack.animation.LocalStackAnimationProvider
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import one.healthium.carnivore.presentation.navigation.main.MainContent
import one.healthium.decompose.DefaultStackAnimationProvider
import one.healthium.login.navigation.login.SignInContent
import one.healthium.login.navigation.magic_link.MagicLinkContent
import one.healthium.login.navigation.silent_login.SilentLoginContent
import one.healthium.onboarding.navigation.OnboardingContent
import one.healthium.paywall.navigation.PaywallContent
import one.healthium.uscreen.navigation.UScreenContent
import one.healthium.ui.common.lifecycle.AppLifecycleDispatcherEffect
import one.healthium.ui.compose.components.AppGlobalProgressIndicator

@Composable
fun RootContent(
    component: RootComponent,
) {
    AppLifecycleDispatcherEffect(component) {
        AppGlobalProgressIndicator {
            CompositionLocalProvider(
                LocalStackAnimationProvider provides DefaultStackAnimationProvider
            ) {
                Box {
                    StackChildren(component)
                    SlotChildren(component)
                }
            }
        }
    }
}

@Composable
private fun StackChildren(component: RootComponent) {
    Children(
        modifier = Modifier.fillMaxSize(),
        stack = component.stack,
    ) {
        when (val child = it.instance) {
            is RootComponent.Child.Main -> MainContent(child.component)
            is RootComponent.Child.SignIn -> SignInContent(child.component)
            is RootComponent.Child.Paywall -> PaywallContent(child.component)
            is RootComponent.Child.Onboarding -> OnboardingContent(child.component)
            is RootComponent.Child.UScreen -> UScreenContent(child.component)
            is RootComponent.Child.SilentLogin -> SilentLoginContent(child.component, false)
            is RootComponent.Child.MagicLink -> MagicLinkContent(child.component)
        }
    }
}

@Composable
private fun SlotChildren(component: RootComponent) {
    val slot by component.slot.subscribeAsState()
    val slotChild = slot.child?.instance

    // this one is needed to remember the child when it's being animated out
    var rememberedChild by remember { mutableStateOf<RootComponent.Child?>(null) }
    if (slotChild != null) {
        rememberedChild = slotChild
    }

    AnimatedVisibility(
        visible = slotChild != null,
        enter = slideInVertically { it },
        exit = slideOutVertically { it }
    ) {
        DisposableEffect(rememberedChild) {
            onDispose {
                rememberedChild = null
            }
        }
        when (val child = rememberedChild) {
            is RootComponent.Child.Main -> MainContent(child.component)
            is RootComponent.Child.SignIn -> SignInContent(child.component)
            is RootComponent.Child.Paywall -> PaywallContent(child.component)
            is RootComponent.Child.Onboarding -> OnboardingContent(child.component)
            is RootComponent.Child.UScreen -> UScreenContent(child.component)
            is RootComponent.Child.SilentLogin -> SilentLoginContent(child.component)
            is RootComponent.Child.MagicLink -> MagicLinkContent(child.component)
            null -> {}
        }
    }
}
